"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const fuzzysearch_1 = __importDefault(require("fuzzysearch"));
const react_1 = require("react");
const lodash_1 = require("lodash");
const common_components_1 = require("@zapier/common-components");
const theme_1 = require("@zapier/theme");
const design_system_1 = require("@zapier/design-system");
const common_1 = require("../../../common");
const common_utils_1 = require("@zapier/common-utils");
const constants_1 = require("../../../../constants");
const utils_1 = require("../../../PrettySelect/utils");
const ChunkingItemRenderer_1 = __importDefault(require("./ChunkingItemRenderer"));
const Option_1 = __importDefault(require("../../../PrettySelect/DropdownMenu/Option"));
const FloatingMenuAlert_1 = __importDefault(require("../../../FloatingMenuAlert"));
const Styles = common_utils_1.labelStyles(__filename, {
    optionLoadingIndicatorContainer: {
        padding: theme_1.Spacing.small,
    },
    optionsContainer: {
        paddingRight: theme_1.Spacing.extraSmall,
        maxHeight: constants_1.MAX_DROPDOWN_OPTIONS_HEIGHT,
        overflowX: 'hidden',
        overflowY: 'auto',
        '::-webkit-scrollbar': {
            background: 'transparent',
            height: theme_1.Spacing.extraSmall,
            width: theme_1.Spacing.extraSmall,
        },
        '::-webkit-scrollbar-thumb': {
            background: design_system_1.Colors.grey300,
            borderRadius: theme_1.Border.radius.default,
        },
    },
    emptyOptionsContainer: {
        padding: `${theme_1.Spacing.medium}px ${theme_1.Spacing.small}px`,
    },
    searchContainer: {
        paddingTop: theme_1.Spacing.medium,
        paddingBottom: theme_1.Spacing.small,
    },
    buttonContainer: {
        paddingTop: theme_1.Spacing.small,
        display: 'flex',
    },
});
const OptionLoadingIndicator = () => (common_utils_1.isolateComponent("div", { css: Styles.optionLoadingIndicatorContainer },
    common_utils_1.isolateComponent(common_components_1.ContentLoader, { height: "49px" },
        common_utils_1.isolateComponent("circle", { cx: "10", cy: "20", r: "10" }),
        common_utils_1.isolateComponent("rect", { height: "20px", width: "120px", x: "30px", y: "0px" }),
        common_utils_1.isolateComponent("rect", { height: "16px", width: "60px", x: "30px", y: "26px" }))));
const OptionsLoadingIndicator = () => (common_utils_1.isolateComponent("div", { css: Styles.optionsContainer },
    common_utils_1.isolateComponent(OptionLoadingIndicator, { key: "0" })));
const OptionsFilteredIndicator = () => (common_utils_1.isolateComponent("div", { css: Styles.emptyOptionsContainer },
    common_utils_1.isolateComponent(design_system_1.Text, { color: "grey500" }, "All available options are filtered \u2026")));
const OptionsEmptyIndicator = () => (common_utils_1.isolateComponent("div", { css: Styles.emptyOptionsContainer },
    common_utils_1.isolateComponent(design_system_1.Text, { color: "grey500" }, "No options available.")));
const OptionsPane = ({ loadMoreState, getItemProps, highlightedIndex, isLoadingMore, items, itemsLabel, onClear, onSelectAndRemainOpen, onLoadOptions, onSelectItem, selectedItem, setHighlightedIndex, }) => {
    const groupId = `floating-menu-group-${Math.random()}`;
    const [query, setQuery] = react_1.useState('');
    const sanitizedQuery = query && query.toLowerCase();
    const isFilterActive = Boolean(sanitizedQuery && sanitizedQuery.length > 0);
    const filterByQuery = (item) => fuzzysearch_1.default(sanitizedQuery, lodash_1.toLower(utils_1.getOptionLabel(item))) ||
        fuzzysearch_1.default(sanitizedQuery, lodash_1.toLower(utils_1.getOptionValue(item)));
    const filteredItems = items.filter(filterByQuery);
    const areItemsEmpty = filteredItems.length === 0 ||
        (filteredItems.length === 1 &&
            Object.values(filteredItems[0]).every(value => value === '' || value === undefined));
    react_1.useEffect(() => {
        setHighlightedIndex(-1);
    }, [setHighlightedIndex]);
    const onChangeQuery = (e) => {
        setQuery(e.target.value);
        setHighlightedIndex(0);
    };
    const onQueryKeyDown = (e) => {
        if (e.keyCode === common_utils_1.keyCodes.ArrowUp) {
            setHighlightedIndex(highlightedIndex > 0 ? highlightedIndex - 1 : filteredItems.length - 1);
        }
        if (e.keyCode === common_utils_1.keyCodes.ArrowDown) {
            setHighlightedIndex((highlightedIndex + 1) % filteredItems.length);
        }
        if (e.keyCode === common_utils_1.keyCodes.Enter) {
            onSelectItem(filteredItems[highlightedIndex]);
        }
    };
    const onLoadMore = (e) => {
        e.preventDefault();
        onLoadOptions(true);
    };
    const loadingMoreLabel = lodash_1.isEmpty(items) ? 'Loading …' : 'Loading More …';
    return (common_utils_1.isolateComponent(react_1.Fragment, null,
        common_utils_1.isolateComponent("div", { css: Styles.searchContainer },
            common_utils_1.isolateComponent(common_1.TextInput, { ariaLabel: `Options for ${itemsLabel}`, autoComplete: "off", autoFocus: true, name: "query", onChange: onChangeQuery, onKeyDown: onQueryKeyDown, placeholder: "Search \u2026", renderIconBefore: ({ iconSize }) => (common_utils_1.isolateComponent(design_system_1.Icon, { name: "magnifyingGlass", size: iconSize })), value: query })),
        loadMoreState === 'all-options-loaded' &&
            !isLoadingMore &&
            !lodash_1.isEmpty(items) && (common_utils_1.isolateComponent(FloatingMenuAlert_1.default, { heading: `We couldn't find any more choices.`, message: `Sorry, there are no more choices. We've loaded all ${lodash_1.size(items)}.` })),
        common_utils_1.isolateComponent(ChunkingItemRenderer_1.default, { css: Styles.optionsContainer, items: filteredItems, renderItem: (item, index) => (common_utils_1.isolateComponent(Option_1.default, { checked: selectedItem &&
                    utils_1.getOptionValue(selectedItem) === utils_1.getOptionValue(item), disabled: isLoadingMore, getItemProps: getItemProps, groupId: groupId, highlighted: index === highlightedIndex, index: index, item: item, itemsLabel: itemsLabel, key: index, onSelectAndRemainOpen: onSelectAndRemainOpen })) }, areItemsEmpty &&
            common_utils_1.cond([isFilterActive, common_utils_1.isolateComponent(OptionsFilteredIndicator, null)], [isLoadingMore, common_utils_1.isolateComponent(OptionsLoadingIndicator, null)], common_utils_1.isolateComponent(OptionsEmptyIndicator, null))),
        common_utils_1.isolateComponent("div", { css: Styles.buttonContainer },
            loadMoreState !== 'can-never-load-more' && (common_utils_1.isolateComponent(react_1.Fragment, null,
                common_utils_1.isolateComponent(design_system_1.Button, { color: "greyAlt", disabled: isLoadingMore || loadMoreState === 'all-options-loaded', isFullWidth: true, onClick: onLoadMore, size: "small" }, isLoadingMore ? loadingMoreLabel : 'Load More'),
                common_utils_1.isolateComponent(design_system_1.Spacer, { width: theme_1.Spacing.medium }))),
            common_utils_1.isolateComponent(design_system_1.Button, { color: "greyAlt", disabled: isLoadingMore, isFullWidth: true, onClick: onClear, size: "small" }, "Clear"))));
};
exports.default = OptionsPane;
